import { html, nothing, render } from "lit-html";
import { keyed } from "lit-html/directives/keyed.js";
import { langId } from "@escapio/shared/l10n/lang-id";
import { track } from "@escapio/portal/tracker/EventTracker";
import { addEventTrackerData } from "@escapio/portal/tracker/EventQueue";
import { loadMapLibre } from "@escapio/portal/components/map/map-libre/load";
import { createMarker } from "@escapio/portal/components/map/map-libre/Marker";
import { nextFrame } from "@escapio/shared/event/next-frame";
import { createMap } from "@escapio/shared/components/maplibre-util";
import {
	getVersion,
	watch,
	unwatch,
	SIZE_MOBILE,
} from "@escapio/portal/dom/screen";
import {
	NEARBY_EVENT_ACTION_SUFFIX,
	trackClickout,
	trackDirectLink,
} from "@escapio/portal/tracker/Clickout";
import "@escapio/portal/components/hotel-list/hotel-element/MobileStage";
import "./PopupContent";
import { NO_PRICES_STATE } from "@escapio/portal/api/prices-state";

const visibleHotels = new WeakMap();
const markers = new WeakMap();
const selected = new WeakMap();
const prices = new WeakMap();

customElements.define(
	"esc-maplibre-hotel-map",
	class extends HTMLElement {
		constructor() {
			super();

			// Public properties
			this.primaryHotels = [];
			this.secondaryHotels = [];

			// Private properties
			markers.set(this, {});
			selected.set(this, null);
			prices.set(this, {});
		}

		async connectedCallback() {
			this.render();

			this.map = await createMap(this.querySelector(".map-handle"), langId, {
				zoom: 15,
				interactive: this.isInteractive,
			});
			this.map.on("click", () => {
				if (selected.get(this)) {
					this.unselect(selected.get(this));
				}
			});

			const visibleIds = visibleHotels.get(this);
			const isVisible = id =>
				visibleIds === undefined || visibleIds.includes(id);

			const { LngLatBounds, LngLat } = await loadMapLibre();
			const bounds = new LngLatBounds();
			for (const hotel of this.primaryHotels) {
				const position = await this.addHotel(hotel, false, isVisible(hotel.id));
				bounds.extend(position);
			}

			let isOnlyOneVisible = this.primaryHotels.length === 1;
			for (const hotel of this.secondaryHotels) {
				const position = await this.addHotel(hotel, true, isVisible(hotel.id));
				if (this.initMapIncludesSecondary && isVisible(hotel.id)) {
					isOnlyOneVisible = false;
					bounds.extend(position);
				}
			}

			if (isOnlyOneVisible) {
				const [hotel] = this.primaryHotels;
				this.map.setCenter(new LngLat(hotel.lng, hotel.lat));
			} else {
				this.map.fitBounds(bounds, { padding: 50, linear: true });
			}

			watch(this, this.onResize.bind(this));

			this.dispatchEvent(new CustomEvent("ready"));
		}

		disconnectedCallback() {
			unwatch(this);
		}

		render() {
			const { popup, hotel } = selected.get(this) || {};

			const template = html`<div class="map-handle"></div>
				<div class="map__mobile-popup-container">
					${popup && !popup.mapLibreInstance
						? keyed(hotel.id, popup.content)
						: nothing}
				</div>`;
			render(template, this, { host: this });
		}

		async addHotel(hotel, secondary, visible) {
			const position = { lat: hotel.lat, lng: hotel.lng };
			const marker = (await createMarker()).setLngLat(position);
			marker.setSecondary(secondary);

			if (visible) {
				this.addMarkerToMap(hotel.id, marker);
			}
			marker.addEventListener("click", event =>
				this.onMarkerClick(marker, hotel.id, event),
			);
			markers.get(this)[hotel.id] = marker;

			return position;
		}

		set visibleHotels(ids) {
			const alreadyVisibleIds =
				visibleHotels.get(this)?.filter(id => ids.includes(id)) || [];
			visibleHotels.set(this, ids);

			if (selected.get(this) && !ids.includes(selected.get(this).hotel.id)) {
				this.unselect(selected.get(this));
			}

			Object.entries(markers.get(this))
				.filter(([id]) => !alreadyVisibleIds.includes(Number.parseInt(id)))
				.forEach(([id, marker]) => {
					if (ids.includes(Number.parseInt(id))) {
						this.addMarkerToMap(id, marker);
					} else {
						marker.remove();
					}
				});
		}

		addMarkerToMap(hotelId, marker) {
			marker.addTo(this.map);

			if (!this.isInteractive) {
				marker.setPrice(NO_PRICES_STATE);
			}

			marker.setPrice(prices.get(this)?.[hotelId] ?? null);
		}

		set prices(pricesObject) {
			prices.set(this, pricesObject);
			const { hotel, popup } = selected.get(this) || {};
			if (popup?.mapLibreInstance) {
				popup.content.prices = pricesObject[hotel.id];
				popup.content.render();
			}

			Object.entries(markers.get(this)).forEach(([id, marker]) => {
				marker.setPrice(pricesObject[id]);
			});
		}

		onMarkerClick(marker, id, event) {
			if (!this.isInteractive) {
				return;
			}
			event.stopPropagation();
			event.preventDefault();

			if (!selected.get(this) || selected.get(this).marker !== marker) {
				const hotel =
					this.primaryHotels.find(h => h.id === id) ||
					this.secondaryHotels.find(h => h.id === id);
				this.select(hotel, marker);

				track(
					"Karte",
					`Overlay geöffnet (${this.trackName}${
						marker.isSecondary ? NEARBY_EVENT_ACTION_SUFFIX : ""
					})`,
					`${hotel.id}${
						marker.isSecondary
							? this.trackHotelSecondaryActionSuffix
							: this.trackHotelPrimaryActionSuffix
					}`,
				);
			}
		}

		async selectHotel(id) {
			const hotel =
				this.primaryHotels.find(h => h.id === id) ||
				this.secondaryHotels.find(h => h.id === id);

			await this.select(hotel, markers.get(this)[id]);
		}

		async select(hotel, marker) {
			if (selected.get(this) !== null) {
				await this.unselect(selected.get(this));
			}

			marker.setActive(true);

			if (getVersion() === SIZE_MOBILE) {
				this.openMobilePopup(hotel, marker);
			} else {
				await this.openMapLibrePopup(hotel, marker);
			}
		}

		async openMapLibrePopup(hotel, marker) {
			const trackName = this.getMarkerTrackName(marker);

			const popupContent = document.createElement("esc-maplibre-popup");
			popupContent.hotel = hotel;
			popupContent.trackName = trackName;

			const hotelPrices = prices.get(this).hasOwnProperty(hotel.id)
				? prices.get(this)[hotel.id]
				: null;
			popupContent.prices = hotelPrices;

			popupContent.addEventListener(
				"PopupClose",
				this.unselect.bind(this, { marker }),
			);

			popupContent.addEventListener(
				"PopupProfileLink",
				this.trackProfileLinkClick(trackName, hotel),
			);

			popupContent.addEventListener("PopupDirectLinkClick", () => {
				trackDirectLink(trackName, hotel, 1, true);
			});

			popupContent.addEventListener("PopupClickout", () => {
				const offer = hotelPrices.offers[0];
				const provider = hotelPrices.providers[offer.provider];
				trackClickout(
					this.trackName,
					hotel,
					offer.provider,
					provider.name,
					offer.total_rate,
					1,
				);
			});

			const { Popup, LngLat, LngLatBounds } = await loadMapLibre();
			const popup = new Popup({
				anchor: "bottom",
				closeButton: false,
				closeOnClick: false,
			}).setDOMContent(popupContent);
			marker.setPopup(popup).togglePopup();

			selected.set(this, {
				marker,
				popup: { mapLibreInstance: popup, content: popupContent },
				hotel,
			});

			await nextFrame();
			const mapRect = this.map.getCanvas().getBoundingClientRect();
			const popRect = popup.getElement().getBoundingClientRect();
			const popLeftOffset = Math.min(popRect.left - mapRect.left, 0);
			const popRightOffset = Math.max(popRect.right - mapRect.right, 0);
			const popupOffsetX = popLeftOffset || popRightOffset;
			const popupOffsetY = Math.max(mapRect.top - popRect.top, 0);
			const mapBounds = this.map.getBounds();
			const mapLongDelta = mapBounds.getEast() - mapBounds.getWest();
			const mapLatDelta = mapBounds.getNorth() - mapBounds.getSouth();

			if (!popupOffsetX && !popupOffsetY) {
				return;
			}

			const longDelta = mapLongDelta * (popupOffsetX / mapRect.width);
			const latDelta = mapLatDelta * (popupOffsetY / mapRect.height);
			const newSW = new LngLat(
				mapBounds.getWest() + longDelta,
				mapBounds.getSouth() + latDelta,
			);
			const newNE = new LngLat(
				mapBounds.getEast() + longDelta,
				mapBounds.getNorth() + latDelta,
			);
			this.map.fitBounds(new LngLatBounds(newSW, newNE), {
				padding: 10,
				speed: 0.4,
			});
		}

		openMobilePopup(hotel, marker) {
			const hotelPrices = prices.get(this)?.[hotel.id];

			selected.set(this, {
				marker,
				popup: {
					content: html`<esc-maplibre-popup
						.hotel="${hotel}"
						.prices="${hotelPrices}"
						.trackName="${this.getMarkerTrackName(marker)}"
					></esc-maplibre-popup>`,
				},
				hotel,
			});
			this.render();
		}

		async onResize(screenSize) {
			const { popup, hotel, marker } = selected.get(this) || {};
			if (!popup) {
				return;
			}
			if (screenSize === SIZE_MOBILE) {
				popup.mapLibreInstance.remove();
				this.openMobilePopup(hotel, marker);
				return;
			}

			if (!popup.mapLibreInstance) {
				await this.openMapLibrePopup(hotel, marker);
				this.render();
			}
		}

		async unselect({ marker, popup }) {
			selected.set(this, null);

			marker.setActive(false).setVisited(true).setPopup(null);
			popup?.mapLibreInstance?.remove();
			this.render();
		}

		trackProfileLinkClick(trackName, hotel) {
			return ({ detail }) => {
				addEventTrackerData(
					"Karte",
					`Klick Profil (${trackName})`,
					`${detail.element} - ${hotel.id} | ${hotel.name}`,
				);
			};
		}

		getMarkerTrackName(marker) {
			return marker.isSecondary
				? `${this.trackName}${NEARBY_EVENT_ACTION_SUFFIX}`
				: this.trackName;
		}

		get isInteractive() {
			return !this.hasAttribute("static");
		}

		get trackName() {
			return this.getAttribute("track-name");
		}

		get trackHotelPrimaryActionSuffix() {
			return this.getAttribute("track-hotel-primary-action-suffix") || "";
		}

		get trackHotelSecondaryActionSuffix() {
			return this.getAttribute("track-hotel-secondary-action-suffix") || "";
		}

		get initMapIncludesSecondary() {
			return this.hasAttribute("init-map-includes-secondary");
		}
	},
);
